import { isEmptyValue } from './bookingGate';
import type { BookEventBranch, BookEventPayload } from './bookEvent.types';
import { EVENT_TYPES } from './bookEventServiceRules';

function canBookClassGuest(branch: BookEventBranch | null): boolean {
  const booking = branch?.features?.booking;
  return [booking?.enabled === true, (booking?.guest_bookings ?? 0) > 0].every(Boolean);
}

export function canBookGuest(
  event: BookEventPayload,
  branch: BookEventBranch | null
): boolean {
  if (isEmptyValue(branch)) {
    return false;
  }
  const guestRules: Partial<Record<NonNullable<BookEventPayload['type']>, () => boolean>> = {
    [EVENT_TYPES.CLASS]: () => canBookClassGuest(branch),
    [EVENT_TYPES.COURSE]: () => (branch?.features?.courses?.guest_bookings ?? 0) > 0,
  };
  const guestRule = guestRules[event.type ?? EVENT_TYPES.EVENT] ?? (() => false);
  return guestRule();
}

function remainingGuestCapacity(event: BookEventPayload): number {
  const capacityRules: Partial<Record<NonNullable<BookEventPayload['type']>, () => number>> = {
    [EVENT_TYPES.CLASS]: () => (event.size ?? 0) - (event.booked ?? 0) - 1,
    [EVENT_TYPES.COURSE]: () =>
      (event.scheduleToBook?.size ?? 0) - (event.scheduleToBook?.booked ?? 0) - 1,
  };
  const capacityRule = capacityRules[event.type ?? EVENT_TYPES.EVENT] ?? (() => 0);
  return capacityRule();
}

function classGuestLimit(branch: BookEventBranch | null): number {
  return branch?.features?.booking?.guest_bookings ?? 0;
}

function courseGuestLimit(branch: BookEventBranch | null): number {
  return branch?.features?.courses?.guest_bookings ?? 0;
}

function configuredGuestLimit(
  event: BookEventPayload,
  branch: BookEventBranch | null
): number {
  const limits: Partial<Record<NonNullable<BookEventPayload['type']>, () => number>> = {
    [EVENT_TYPES.CLASS]: () => classGuestLimit(branch),
    [EVENT_TYPES.COURSE]: () => courseGuestLimit(branch),
  };
  const guestLimit = limits[event.type ?? EVENT_TYPES.EVENT] ?? (() => 0);
  return guestLimit();
}

export function maxGuests(event: BookEventPayload, branch: BookEventBranch | null): number {
  const capacity = Math.max(0, remainingGuestCapacity(event));
  const branchLimit = configuredGuestLimit(event, branch);
  if (branchLimit <= 0) {
    return capacity;
  }
  return Math.min(capacity, branchLimit);
}
